import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UserCircle, SealCheck, Flower, Users, UsersThree, Plus, ChatCircleDots, Clock, Heart, Sparkle, Trophy } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import AddPersonDialog from "@/components/AddPersonDialog";

const PRIMARY = [
  { v: "all", label: "All" },
  { v: "current", label: "Current" },
  { v: "retired", label: "Retired" },
  { v: "deceased", label: "In Memoriam" },
];
const CATEGORIES = [
  { v: "teaching", label: "Teaching" },
  { v: "leadership", label: "Leadership" },
  { v: "non_teaching", label: "Non-Teaching" },
];

const VBADGE = { institution: "Institution", colleague: "Colleague", alumni: "Alumni", documentation: "Documentation" };

export function InstitutionPeople({ institutionId, institutionName, canManage }) {
  const { user } = useAuth();
  const [filter, setFilter] = useState("all");
  const [decade, setDecade] = useState("");
  const [data, setData] = useState(null);
  const [decades, setDecades] = useState([]);
  const [groups, setGroups] = useState([]);
  const [recognition, setRecognition] = useState([]);

  const load = useCallback(() => {
    const params = { filter };
    if (decade) params.decade = decade;
    api.get(`/institutions/${institutionId}/people`, { params }).then((r) => setData(r.data)).catch(() => setData({ total: 0, people: [], counts: {} }));
  }, [institutionId, filter, decade]);

  const loadGroups = useCallback(() => {
    api.get(`/public/institutions/${institutionId}/faculty-groups`).then((r) => setGroups(r.data || [])).catch(() => setGroups([]));
  }, [institutionId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get(`/institutions/${institutionId}/faculty-decades`).then((r) => setDecades(r.data.decades || [])).catch(() => {});
    api.get(`/institutions/${institutionId}/recognition`).then((r) => setRecognition(r.data.people || [])).catch(() => {});
    loadGroups();
  }, [institutionId, loadGroups]);

  return (
    <div data-testid="institution-people">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading text-xl font-medium">Faculty &amp; Staff</h3>
          <p className="text-sm text-muted-foreground">Teachers, faculty &amp; staff who built this institution — living and remembered.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/find-teacher" className="text-sm font-medium text-primary hover:underline" data-testid="find-teacher-link">Find my teacher →</Link>
          {user && <CreateFacultyGroupDialog institutionId={institutionId} institutionName={institutionName} onCreated={loadGroups} />}
          <AddPersonDialog institutionId={institutionId} institutionName={institutionName} onCreated={() => load()} />
        </div>
      </div>

      {/* Primary status sub-tabs */}
      <div className="mt-4 flex flex-wrap gap-2" data-testid="people-filters">
        {PRIMARY.map((f) => (
          <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`people-filter-${f.v}`}
            className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${filter === f.v ? "border-secondary bg-secondary text-secondary-foreground" : "border-border bg-background text-muted-foreground hover:border-secondary/50"}`}>
            {f.label}{f.v !== "all" && data?.counts?.[f.v] != null ? <span className="ml-1 opacity-70">{data.counts[f.v]}</span> : null}
          </button>
        ))}
        <span className="mx-1 self-center text-border">|</span>
        {CATEGORIES.map((f) => (
          <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`people-filter-${f.v}`}
            className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${filter === f.v ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background text-muted-foreground hover:border-primary/50"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Faculty History Timeline (decades) */}
      {decades.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="faculty-decades">
          <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock size={13} /> History timeline</span>
          {decades.map((d) => (
            <button key={d.decade} onClick={() => setDecade(decade === d.decade ? "" : d.decade)} data-testid={`faculty-decade-${d.decade}`}
              className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${decade === d.decade ? "border-green-600 bg-green-600 text-white" : "border-slate-200 text-muted-foreground hover:border-green-400"}`}>
              {d.decade}s <span className="opacity-70">{d.count}</span>
            </button>
          ))}
        </div>
      )}

      {/* Faculty & Staff groups */}
      {groups.length > 0 && (
        <div className="mt-5 rounded-2xl border border-border/60 bg-muted/30 p-4" data-testid="faculty-groups">
          <p className="mb-2 flex items-center gap-1.5 text-sm font-medium"><UsersThree size={16} className="text-secondary" weight="duotone" /> Faculty &amp; staff groups</p>
          <div className="flex flex-wrap gap-2">
            {groups.map((g) => (
              <Link key={g.id} to={`/groups/${g.id}`} data-testid={`faculty-group-${g.id}`}
                className="flex items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1.5 text-sm transition-colors hover:border-secondary">
                <ChatCircleDots size={14} className="text-secondary" /> {g.name}
                <span className="text-xs text-muted-foreground">· {g.member_count}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {recognition.length > 0 && (
        <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50/60 p-4" data-testid="recognition-wall">
          <p className="mb-3 flex items-center gap-1.5 text-sm font-medium text-amber-900"><Trophy size={16} weight="duotone" className="text-amber-600" /> Recognition Wall <span className="text-xs font-normal text-amber-700">— teachers our alumni honour most</span></p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {recognition.slice(0, 6).map((r) => (
              <Link key={r.id} to={`/person/${r.id}`} data-testid={`recognition-person-${r.id}`}
                className="group flex items-center gap-3 rounded-xl border border-amber-200 bg-white p-2.5 transition-all hover:-translate-y-0.5 hover:shadow-sm">
                <span className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-lg bg-amber-100">
                  {r.photo_url ? <img src={r.photo_url} alt="" className="h-full w-full object-cover" /> : <Sparkle size={16} weight="fill" className="text-amber-600" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium group-hover:text-primary">{r.name}</span>
                  <span className="block truncate text-[11px] text-amber-800">{r.labels[0]?.label_text}{r.total > 0 ? ` · ${r.total} recognition${r.total === 1 ? "" : "s"}` : ""}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {data === null ? <p className="mt-6 text-sm text-muted-foreground">Loading…</p>
        : data.people.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-dashed border-border/60 p-8 text-center">
            <Users size={28} className="mx-auto text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">No people added yet for this filter. Be the first to honour a teacher or staff member.</p>
          </div>
        ) : (
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {data.people.map((pp) => (
              <Link key={pp.id} to={`/person/${pp.id}`} data-testid={`people-card-${pp.id}`}
                className="group flex items-start gap-3 rounded-xl border border-slate-100 bg-white p-3 shadow-sm transition-all hover:-translate-y-0.5 hover:border-green-300 hover:shadow-md">
                <span className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-lg bg-gradient-to-br from-green-50 to-orange-50">
                  {pp.photo_url ? <img src={pp.photo_url} alt="" className="h-full w-full object-cover" /> : <UserCircle size={24} weight="duotone" className="text-green-600" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1 text-sm font-medium text-foreground group-hover:text-primary">
                    {pp.name}
                    {pp.status === "deceased" && <Flower size={13} weight="fill" className="shrink-0 text-primary" title="In Memoriam" />}
                    {pp.verified && <SealCheck size={12} weight="fill" className="shrink-0 text-primary" />}
                  </span>
                  <span className="mt-0.5 block truncate text-xs text-muted-foreground">{pp.role_here || pp.title || pp.subcategory}</span>
                  <span className="mt-1 flex flex-wrap items-center gap-1">
                    {pp.status === "deceased" && <Badge variant="outline" className="rounded-full border-primary/20 bg-primary/5 text-[10px] font-normal text-primary">In Memoriam</Badge>}
                    {(pp.verification_badges || []).slice(0, 2).map((b) => (
                      <Badge key={b.level} variant="outline" className="rounded-full border-secondary/30 bg-secondary/5 text-[10px] font-normal text-secondary" data-testid={`vbadge-${b.level}`}>{VBADGE[b.level]} ✓</Badge>
                    ))}
                    {pp.remembered_count > 0 && <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground"><Heart size={10} weight="fill" className="text-rose-400" /> {pp.remembered_count}</span>}
                  </span>
                </span>
              </Link>
            ))}
          </div>
        )}
    </div>
  );
}

const GROUP_PRESETS = [
  "Current Teachers", "Retired Teachers Association", "Former Principals",
  "Science Department Teachers", "Physical Education Staff", "School Administration Team",
];

function CreateFacultyGroupDialog({ institutionId, institutionName, onCreated }) {
  const nav = null;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ name: "", description: "", privacy: "public" });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) { toast.error("Give the group a name"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/groups", {
        name: f.name.trim(), description: f.description, type: "faculty",
        institution_id: institutionId, faculty: true, privacy: f.privacy,
      });
      toast.success("Group created");
      setOpen(false); setF({ name: "", description: "", privacy: "public" });
      if (onCreated) onCreated(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not create group"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full gap-1.5" data-testid="create-faculty-group-open"><UsersThree size={15} /> Create a group</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="create-faculty-group-dialog">
        <DialogHeader>
          <DialogTitle>Create a Faculty &amp; Staff group</DialogTitle>
          <DialogDescription>A space for teachers &amp; staff of <strong>{institutionName}</strong> to discuss, plan activities and stay connected.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Group name</Label>
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1" placeholder={`${institutionName} — Retired Teachers`} data-testid="faculty-group-name" />
            <div className="mt-2 flex flex-wrap gap-1.5">
              {GROUP_PRESETS.map((p) => (
                <button type="button" key={p} onClick={() => setF({ ...f, name: `${institutionName} — ${p}` })}
                  className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:border-secondary">{p}</button>
              ))}
            </div>
          </div>
          <div><Label className="text-xs">Description</Label><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1" placeholder="What is this group for?" data-testid="faculty-group-desc" /></div>
          <div>
            <Label className="text-xs">Privacy</Label>
            <Select value={f.privacy} onValueChange={(v) => setF({ ...f, privacy: v })}>
              <SelectTrigger className="mt-1" data-testid="faculty-group-privacy"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public</SelectItem>
                <SelectItem value="institution">Institution-only</SelectItem>
                <SelectItem value="invite">Invitation-only</SelectItem>
                <SelectItem value="private">Private</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter><Button type="submit" disabled={busy} className="rounded-full gap-1.5" data-testid="faculty-group-submit"><Plus size={15} /> {busy ? "Creating…" : "Create group"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default InstitutionPeople;
